import { useEffect, useImperativeHandle, useRef, useState } from 'react'

import {
  DSAlert,
  DSButton,
  DSCircularLoading,
  DSCol,
  DSModal,
  DSRow,
  DSText
} from '@vr/ds-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { FormFilterRef, ModalConfirmationDeliveryProps } from './interface'
import { BoxRounded } from './styled'
import { FormValues } from './type'
import { DeliveryDate } from '@/compositions/delivery-date'
import { ResponsibleReceiving } from '@/compositions/responsibleReceiving'
import { useDeliveryContext } from '@/contexts/delivery.context'
import { useDeliveryPatch } from '@/hooks/use-delivery-patch'
import { formatBrDateToIso } from '@/utils/format-br-date-to-iso'

const DEFAULT_VALUES: FormValues = {
  agrupamentoId: null,
  comprovanteDeEntrega: null,
  dataDaEntrega: '',
  responsavelRecebimento: null
}

const ModalConfirmationDelivery = ({
  onClose = () => {},
  delivery,
  isOpen,
  setShowErrorModal,
  setShowSuccessModal
}: ModalConfirmationDeliveryProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const imperativeRef = useRef<FormFilterRef>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [accountKey, setAccountKey] = useState(Date.now())
  const [fileUploaded, setFileUploaded] = useState<File | null>(null)
  const [errorUploaded, setErrorUploaded] = useState<string | null>(null)

  const { handleFilter, currentFilters } = useDeliveryContext()
  const { deliveryPatch, isLoading } = useDeliveryPatch()

  const userString = localStorage.getItem('user')
  const user = userString ? JSON.parse(userString) : {}
  const userId = user?.userId

  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  })

  useEffect(() => {
    setAccountKey(Date.now())
  }, [])

  useImperativeHandle(imperativeRef, () => ({
    submitForm: () => {
      formRef.current?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      )
    }
  }))

  const handleButtonClick = () => {
    fileInputRef.current.click()
  }

  const onSubmit = async (data: FormValues) => {
    deliveryPatch({
      agrupamentoId: delivery.id,
      alteradoPor: userId,
      dataDaEntrega: formatBrDateToIso(data.dataDaEntrega ?? null),
      recebidoPor: data.responsavelRecebimento,
      comprovanteDeEntrega: fileUploaded
    })
      .then(response => {
        if (response.sucesso) {
          setShowSuccessModal(true)
          handleFilter(currentFilters)
        } else {
          setShowErrorModal(true)
        }
      })
      .catch(error => {
        console.error('Erro ao confirmar entrega:', error)
        setShowErrorModal(true)
      })
      .finally(() => {
        onClose()
        methods.reset()
        setFileUploaded(null)
      })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    if (file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        setErrorUploaded(
          'O arquivo selecionado excede o tamanho máximo permitido de 10MB.'
        )
        setFileUploaded(null)
        return
      }
      setFileUploaded(file)
      methods.setValue('comprovanteDeEntrega', file, { shouldValidate: true })
    }
  }

  return (
    <DSModal config={{ show: isOpen, onClose }} css={{ width: '600px' }}>
      <FormProvider {...methods}>
        <form
          id='form-request-logistics'
          onSubmit={methods.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <DSModal.Header onClose={onClose}>Confirmar entrega</DSModal.Header>
          {isLoading ? (
            <>
              <DSModal.Body>
                <DSCircularLoading isLoading shouldBlur={false} />
              </DSModal.Body>
              <DSModal.Footer></DSModal.Footer>
            </>
          ) : (
            <DSModal.Body>
              <DSRow wrap='wrap' align='center' css={{ display: 'flex' }}>
                <DSCol xs={12} gap={4} css={{ marginBottom: '$sm16' }}>
                  <DeliveryDate methods={methods} accountKey={accountKey} />
                </DSCol>
                <DSCol xs={12} gap={4} css={{ marginBottom: '$sm16' }}>
                  <ResponsibleReceiving
                    methods={methods}
                    accountKey={accountKey}
                  />
                </DSCol>
                <DSCol xs={12} gap={4}>
                  <DSText as='p' size='sm' weight='bold'>
                    Anexar arquivo de liberação*
                  </DSText>
                  <Controller
                    key={accountKey}
                    name='comprovanteDeEntrega'
                    control={methods.control}
                    rules={{
                      required: 'Comprovante de entrega é obrigatório'
                    }}
                    render={() => (
                      <BoxRounded
                        css={{
                          border: '1px solid $neutralVeryDark',
                          padding: '$sm16',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '$sm16',
                          marginBottom: '$sm24'
                        }}
                      >
                        <DSButton
                          variant='secondary'
                          onClick={handleButtonClick}
                        >
                          Escolher arquivo
                        </DSButton>
                        <input
                          type='file'
                          onChange={handleFileChange}
                          accept='.xls,.xlsx'
                          multiple={false}
                          style={{ display: 'none' }}
                          ref={fileInputRef}
                        />
                        <div className='self-center'>
                          {fileUploaded && (
                            <DSAlert
                              type='success'
                              hiddenBtnClose
                              css={{ padding: '$sm8 $sm20' }}
                            >
                              {fileUploaded?.name} -{' '}
                              {Math.round(fileUploaded.size / 1024)} KB
                            </DSAlert>
                          )}
                          {errorUploaded && (
                            <DSAlert
                              type='error'
                              hiddenBtnClose
                              css={{ padding: '$sm8 $sm20' }}
                            >
                              {errorUploaded}
                            </DSAlert>
                          )}
                        </div>
                      </BoxRounded>
                    )}
                  />
                  {typeof methods.formState.errors.comprovanteDeEntrega
                    ?.message === 'string' && (
                    <DSText
                      as='span'
                      size='sm'
                      weight='semibold'
                      color='errorLight'
                    >
                      {methods.formState.errors.comprovanteDeEntrega.message}
                    </DSText>
                  )}
                </DSCol>
              </DSRow>
            </DSModal.Body>
          )}
          <DSModal.Footer>
            <DSRow wrap='wrap' align='center'>
              <DSCol
                xs={12}
                lg={12}
                gap={4}
                direction='row'
                style={{ display: 'flex' }}
                justify='end'
              >
                <DSButton variant='ghost' outlined onClick={onClose}>
                  Cancelar
                </DSButton>
                <DSButton variant='primary' type='submit'>
                  Confirmar
                </DSButton>
              </DSCol>
            </DSRow>
          </DSModal.Footer>
        </form>
      </FormProvider>
    </DSModal>
  )
}

export default ModalConfirmationDelivery
